import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCalendar } from '@/lib/queries';

const TYPE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  VACATION: { bg: 'bg-primary-subtle', text: 'text-primary', label: 'Vacaciones' },
  MEDICAL: { bg: 'bg-red-50', text: 'text-error', label: 'Permiso médico' },
  PERSONAL: { bg: 'bg-yellow-50', text: 'text-warning', label: 'Permiso personal' },
  MATERNITY: { bg: 'bg-accent-light', text: 'text-accent', label: 'Maternidad/Paternidad' },
  PATERNITY: { bg: 'bg-accent-light', text: 'text-accent', label: 'Maternidad/Paternidad' },
  BEREAVEMENT: { bg: 'bg-purple-50', text: 'text-purple-700', label: 'Duelo' },
  STUDY: { bg: 'bg-blue-50', text: 'text-info', label: 'Estudios' },
  OTHER: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Otro' },
};

export function CalendarPage() {
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState<'mes' | 'semana'>('mes');

  const { from, to, days } = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startWeekday = (first.getDay() + 6) % 7;
    const cells: (Date | null)[] = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return {
      from: first.toISOString().slice(0, 10),
      to: last.toISOString().slice(0, 10),
      days: cells,
    };
  }, [cursor]);

  const { data } = useCalendar(from, to);
  const monthLabel = cursor
    .toLocaleDateString('es', { month: 'long', year: 'numeric' })
    .replace(/^./, (c) => c.toUpperCase());

  const getEventsForDay = (date: Date) => {
    if (!data) return { leaves: [] as any[], holidays: [] as any[] };
    const iso = date.toISOString().slice(0, 10);
    const leaves = data.leaves.filter((l: any) => {
      const start = l.startDate.slice(0, 10);
      const end = l.endDate.slice(0, 10);
      return iso >= start && iso <= end;
    });
    const holidays = data.holidays.filter((h: any) => h.date.slice(0, 10) === iso);
    return { leaves, holidays };
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-font-primary">Calendario de Equipo</h1>
        <div className="flex items-center gap-2">
          <button className="flex h-9 items-center gap-1 rounded-md border border-border bg-white px-3 text-sm font-semibold text-font-secondary hover:bg-bg">
            Solo mi equipo
          </button>
          <button className="flex h-9 items-center gap-1 rounded-md border border-border bg-white px-3 text-sm font-semibold text-font-secondary hover:bg-bg">
            Todas las áreas
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-border p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() =>
                setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))
              }
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary hover:bg-bg"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="min-w-[160px] text-center text-base font-semibold capitalize text-font-primary">
              {monthLabel}
            </span>
            <button
              onClick={() =>
                setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))
              }
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary hover:bg-bg"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          <div className="flex rounded-md border border-border bg-bg p-0.5">
            <button
              onClick={() => setView('mes')}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
                view === 'mes' ? 'bg-primary text-white' : 'text-font-secondary'
              }`}
            >
              Mes
            </button>
            <button
              onClick={() => setView('semana')}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
                view === 'semana' ? 'bg-primary text-white' : 'text-font-secondary'
              }`}
            >
              Semana
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 border-b border-border text-center text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
          {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d) => (
            <div key={d} className="py-3">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            if (!d)
              return (
                <div
                  key={i}
                  className="h-28 border-b border-r border-border-light bg-bg/30"
                />
              );
            const { leaves, holidays } = getEventsForDay(d);
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            return (
              <div
                key={i}
                className={`relative h-28 overflow-hidden border-b border-r border-border-light p-2 ${
                  isWeekend ? 'bg-bg/40' : 'bg-white'
                }`}
              >
                <div
                  className={`mb-1 text-xs font-semibold ${
                    isWeekend ? 'text-font-tertiary' : 'text-font-primary'
                  }`}
                >
                  {d.getDate()}
                </div>
                <div className="flex flex-col gap-0.5">
                  {holidays.map((h: any) => (
                    <div
                      key={h.id}
                      className="truncate rounded bg-purple-50 px-1 text-[10px] font-semibold text-purple-700"
                    >
                      🎉 {h.name}
                    </div>
                  ))}
                  {leaves.slice(0, 2).map((l: any) => {
                    const s = TYPE_STYLES[l.type] ?? TYPE_STYLES.OTHER;
                    return (
                      <div
                        key={l.id}
                        className={`truncate rounded px-1 text-[10px] font-semibold ${s.bg} ${s.text}`}
                        title={`${l.employee.firstName} ${l.employee.lastName}`}
                      >
                        {l.employee.firstName[0]}
                        {l.employee.lastName[0]} {s.label}
                      </div>
                    );
                  })}
                  {leaves.length > 2 && (
                    <div className="text-[10px] text-font-tertiary">
                      +{leaves.length - 2} más
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Leyenda */}
        <div className="flex flex-wrap items-center gap-5 border-t border-border px-6 py-4 text-xs">
          {[
            ['Vacaciones', 'bg-primary-subtle'],
            ['Permiso médico', 'bg-red-50'],
            ['Permiso personal', 'bg-yellow-50'],
            ['Maternidad/Paternidad', 'bg-accent-light'],
            ['Duelo', 'bg-purple-50'],
          ].map(([label, bg]) => (
            <div key={label} className="flex items-center gap-2">
              <span className={`h-3 w-3 rounded ${bg}`} />
              <span className="text-font-secondary">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
